import printJS from "print-js";
// @ts-ignore
import html2pdf from "html2pdf.js";

export type PrintHtmlOptions = {
  printable: string;
  type?: string;
  maxWidth?: number;
  style?: string;
  scanStyles?: boolean;
  targetStyles?: string[];
  documentTitle?: string;
};

export type DownloadHtmlOptions = {
  filename?: string;
  margin?: number | number[];
  /** Extra html2canvas overrides */
  html2canvas?: Record<string, unknown>;
  jsPDF?: Record<string, unknown>;
};

const SHEET_ID = "hmis-ios-pwa-print-sheet";
const BUSY_ID = "hmis-ios-pwa-print-busy";
const A4_PX = 794;

/** Keep images/tables inside printable width (fixes right-side cut-off). */
const FIT_CONTENT_CSS = `
  img, svg, canvas, video {
    max-width: 100% !important;
    height: auto !important;
    object-fit: contain !important;
  }
  table { max-width: 100% !important; }
  * { box-sizing: border-box; }
`;

/**
 * Any iPhone / iPad (Safari tab or Home Screen PWA).
 */
export function isIosDevice(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const nav = navigator as Navigator & { standalone?: boolean };
  const ua = nav.userAgent || "";
  return (
    /iPad|iPhone|iPod/i.test(ua) ||
    (nav.platform === "MacIntel" && nav.maxTouchPoints > 1)
  );
}

/**
 * iPhone / iPad opened via Safari → Add to Home Screen (standalone PWA).
 * Normal Safari tabs return false — they keep using print-js.
 */
export function isIosHomeScreenPwa(): boolean {
  if (!isIosDevice()) return false;
  const nav = navigator as Navigator & { standalone?: boolean };
  return (
    nav.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

function withPrintableClone<T>(
  element: HTMLElement,
  run: (clone: HTMLElement) => Promise<T>,
): Promise<T> {
  const host = document.createElement("div");
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${A4_PX}px;background:#fff;overflow:visible;z-index:-1;`;
  const clone = element.cloneNode(true) as HTMLElement;
  clone.style.cssText = `width:${A4_PX}px;max-width:${A4_PX}px;transform:none!important;overflow:visible!important;`;
  host.appendChild(clone);
  document.body.appendChild(host);

  return run(clone).finally(() => {
    try {
      host.remove();
    } catch {
      // ignore
    }
  });
}

async function elementToPdfDataUrl(
  element: HTMLElement,
  options: DownloadHtmlOptions = {},
): Promise<string> {
  const zoomParent = element.parentElement as HTMLElement | null;
  const prevTransform = zoomParent?.style.transform ?? "";
  if (zoomParent) zoomParent.style.transform = "none";

  try {
    return await withPrintableClone(element, async (clone) => {
      const scale = isIosDevice()
        ? Math.min(2.5, Math.max(2, window.devicePixelRatio || 2))
        : Math.min(3, Math.max(2, (window.devicePixelRatio || 1) * 2));

      const dataUrl: string = await html2pdf()
        .set({
          margin: options.margin ?? [8, 8, 8, 8],
          image: { type: "jpeg", quality: 0.98 },
          html2canvas: {
            scale,
            scrollY: 0,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
            width: A4_PX,
            windowWidth: A4_PX,
            ...(options.html2canvas || {}),
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
            ...(options.jsPDF || {}),
          },
          pagebreak: { mode: ["css", "legacy"] },
        })
        .from(clone)
        .outputPdf("datauristring");
      return dataUrl;
    });
  } finally {
    if (zoomParent) zoomParent.style.transform = prevTransform;
  }
}

async function dataUrlToPdfFile(dataUrl: string, title: string): Promise<File> {
  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const safeName = (title || "document").replace(/[\\/:*?"<>|]+/g, "_");
  return new File([blob], `${safeName}.pdf`, { type: "application/pdf" });
}

/**
 * Open iOS share sheet with PDF. Must run from a fresh user tap
 * (async PDF generation often expires the first Print tap).
 */
async function sharePdfFile(file: File, title: string): Promise<"ok" | "cancel" | "blocked"> {
  try {
    const nav = navigator as Navigator & {
      canShare?: (data: ShareData) => boolean;
      share?: (data: ShareData) => Promise<void>;
    };
    if (!nav.share) return "blocked";
    if (nav.canShare && !nav.canShare({ files: [file] })) return "blocked";
    await nav.share({ files: [file], title: title || "Print" });
    return "ok";
  } catch (e: any) {
    if (e?.name === "AbortError") return "cancel";
    // NotAllowedError = gesture expired / blocked
    return "blocked";
  }
}

function showBusy(on: boolean, message = "Preparing…") {
  let el = document.getElementById(BUSY_ID);
  if (!on) {
    el?.remove();
    return;
  }
  if (el) {
    const label = el.querySelector("[data-msg]");
    if (label) label.textContent = message;
    return;
  }
  el = document.createElement("div");
  el.id = BUSY_ID;
  el.style.cssText =
    "position:fixed;inset:0;z-index:2147483646;background:rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center;";
  el.innerHTML = `<div data-msg style="background:#fff;padding:16px 22px;border-radius:12px;font:600 15px -apple-system,Arial;color:#222;">${message}</div>`;
  document.body.appendChild(el);
}

function removePrintSheet() {
  document.getElementById(SHEET_ID)?.remove();
  showBusy(false);
}

/**
 * OPD Detail print opens inside MUI Dialog. If the sheet is only on
 * document.body, iOS PWA focus-trap / aria-hidden blocks taps — List
 * often works, Detail does not. Mount inside the open modal instead.
 */
function getPrintSheetHost(sourceEl: HTMLElement): HTMLElement {
  const modal =
    (sourceEl.closest(".MuiModal-root, .MuiDialog-root") as HTMLElement | null) ||
    (document.querySelector(
      ".MuiModal-root[role='presentation']:last-of-type, .MuiDialog-root:last-of-type",
    ) as HTMLElement | null);
  return modal || document.body;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = window.setTimeout(
      () => reject(new Error(`${label} timed out`)),
      ms,
    );
    promise.then(
      (v) => {
        window.clearTimeout(t);
        resolve(v);
      },
      (e) => {
        window.clearTimeout(t);
        reject(e);
      },
    );
  });
}

/**
 * FINAL iOS Home Screen PWA print path (always works):
 *
 * Apple blocks window.print() / iframe print in standalone PWA (iOS 27+).
 * Reliable path: prepare PDF → Print button → Share sheet → Print.
 * Desktop / Android / Safari tab are untouched (print-js).
 */
function openIosPwaPrintSheet(
  element: HTMLElement,
  options: PrintHtmlOptions,
  onClose?: () => void,
): void {
  removePrintSheet();

  const title = options.documentTitle || "Print";
  let pdfFile: File | null = null;
  let preparing: Promise<File> | null = null;
  let closed = false;

  const finishClose = () => {
    if (closed) return;
    closed = true;
    removePrintSheet();
    try {
      onClose?.();
    } catch {
      // ignore
    }
  };

  const host = getPrintSheetHost(element);
  const sheet = document.createElement("div");
  sheet.id = SHEET_ID;
  sheet.style.cssText =
    "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.45);display:flex;align-items:flex-end;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,Arial,sans-serif;pointer-events:auto;";
  sheet.innerHTML = `
    <div style="width:100%;max-width:480px;background:#fff;border-radius:16px 16px 0 0;padding:18px 16px 28px;box-shadow:0 -8px 32px rgba(0,0,0,.2);">
      <div style="font-size:17px;font-weight:700;color:#111;margin-bottom:6px;">Print</div>
      <div data-status style="font-size:13px;color:#555;line-height:1.4;margin-bottom:14px;">
        Preparing document…
      </div>
      <button type="button" data-print disabled style="width:100%;padding:15px;font-size:17px;font-weight:700;background:#1976d2;color:#fff;border:0;border-radius:12px;opacity:.55;">
        Print
      </button>
      <button type="button" data-close style="width:100%;padding:12px;margin-top:8px;font-size:15px;font-weight:600;background:transparent;color:#444;border:0;">
        Cancel
      </button>
    </div>
  `;
  host.appendChild(sheet);

  const statusEl = sheet.querySelector("[data-status]") as HTMLElement;
  const printBtn = sheet.querySelector("[data-print]") as HTMLButtonElement;
  const closeBtn = sheet.querySelector("[data-close]") as HTMLButtonElement;

  const setReady = () => {
    if (closed) return;
    printBtn.disabled = false;
    printBtn.style.opacity = "1";
    statusEl.textContent =
      "Print dabao → Share sheet me Print choose karo.";
  };

  const setError = (msg: string) => {
    if (closed) return;
    printBtn.disabled = false;
    printBtn.style.opacity = "1";
    statusEl.textContent = msg;
  };

  closeBtn.addEventListener("click", () => finishClose());

  // Detail pages are heavy — lower scale on iOS PWA to avoid hang / "Preparing…" forever
  preparing = (async () => {
    const dataUrl = await withTimeout(
      elementToPdfDataUrl(element, {
        html2canvas: isIosHomeScreenPwa()
          ? { scale: 1.75, logging: false }
          : undefined,
      }),
      45_000,
      "PDF prepare",
    );
    const file = await dataUrlToPdfFile(dataUrl, title);
    pdfFile = file;
    return file;
  })()
    .then((file) => {
      setReady();
      return file;
    })
    .catch(() => {
      setError(
        "Document prepare fail / slow. Print dubara dabao ya Zoom 100% karke try karo.",
      );
      throw new Error("pdf-prepare-failed");
    });

  printBtn.addEventListener("click", async () => {
    if (closed) return;
    printBtn.disabled = true;
    printBtn.textContent = "Opening…";
    statusEl.textContent = "Share sheet khol rahe hain…";

    try {
      const file = pdfFile || (await preparing!);
      const result = await sharePdfFile(file, title);

      if (result === "ok" || result === "cancel") {
        finishClose();
        return;
      }

      printBtn.disabled = false;
      printBtn.textContent = "Print";
      statusEl.textContent =
        "Share sheet block ho gaya. Print ek baar aur dabao.";
    } catch {
      printBtn.disabled = false;
      printBtn.textContent = "Print";
      statusEl.textContent = "Fail hua. Print dubara dabao.";
    }
  });
}

/**
 * - Safari tab + desktop + Android: print-js (unchanged).
 * - iOS Home Screen PWA ONLY: reliable Share→Print sheet (no window.print).
 */
export function printHtmlContent(
  element: HTMLElement | null | undefined,
  options: PrintHtmlOptions,
): void {
  if (!element && !options.printable) return;

  const resolved: PrintHtmlOptions = {
    ...options,
    printable:
      element?.innerHTML && element.innerHTML.length > 0
        ? element.innerHTML
        : options.printable,
    type: options.type || "raw-html",
    style: `${FIT_CONTENT_CSS}\n${options.style || ""}`,
  };

  if (!isIosHomeScreenPwa()) {
    (printJS as (cfg: PrintHtmlOptions) => void)(resolved);
    return;
  }

  if (element) {
    openIosPwaPrintSheet(element, resolved);
    return;
  }

  const temp = document.createElement("div");
  temp.style.cssText = `position:fixed;left:-9999px;top:0;width:${A4_PX}px;background:#fff;`;
  temp.innerHTML = resolved.printable;
  document.body.appendChild(temp);
  openIosPwaPrintSheet(temp, resolved, () => {
    try {
      temp.remove();
    } catch {
      // ignore
    }
  });
}

/** Trigger a direct .pdf file download (blob + anchor). */
async function downloadPdfDirect(dataUrl: string, title: string): Promise<void> {
  const safeName = (title || "document").replace(/[\\/:*?"<>|]+/g, "_");
  const filename = `${safeName}.pdf`;

  const res = await fetch(dataUrl);
  const blob = await res.blob();
  const pdfBlob =
    blob.type === "application/pdf"
      ? blob
      : new Blob([blob], { type: "application/pdf" });
  const url = URL.createObjectURL(pdfBlob);

  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();

    // iOS ignores <a download> — open the PDF itself (native viewer / Save)
    if (isIosDevice()) {
      const opened = window.open(url, "_blank");
      if (!opened) {
        showOneTapOpenPdfFallback(url, filename);
      }
    }
  } finally {
    window.setTimeout(() => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    }, 60_000);
  }
}

function showOneTapOpenPdfFallback(url: string, filename: string) {
  const id = "hmis-ios-pwa-print-fallback";
  document.getElementById(id)?.remove();

  const wrap = document.createElement("div");
  wrap.id = id;
  wrap.style.cssText =
    "position:fixed;left:12px;right:12px;bottom:24px;z-index:2147483647;";
  wrap.innerHTML = `
    <button type="button" style="width:100%;padding:16px;font-size:17px;font-weight:700;background:#1976d2;color:#fff;border:0;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.25);">
      Open PDF
    </button>
  `;
  document.body.appendChild(wrap);

  const btn = wrap.querySelector("button") as HTMLButtonElement;
  btn.setAttribute("aria-label", `Open ${filename}`);
  btn.addEventListener("click", () => {
    const w = window.open(url, "_blank");
    if (!w) window.location.href = url;
    wrap.remove();
  });
}

/**
 * Download HTML node as a PDF file directly (no share sheet).
 * Desktop/Android: saves .pdf. iOS: downloads when allowed, else opens PDF viewer.
 */
export async function downloadHtmlAsPdf(
  element: HTMLElement | null | undefined,
  options: DownloadHtmlOptions = {},
): Promise<void> {
  if (!element) return;

  const rawName = (options.filename || "document.pdf").replace(/\.pdf$/i, "");
  const title = rawName.replace(/[\\/:*?"<>|]+/g, "_") || "document";

  showBusy(true, "Preparing PDF…");
  try {
    const dataUrl = await elementToPdfDataUrl(element, options);
    await downloadPdfDirect(dataUrl, title);
  } finally {
    showBusy(false);
  }
}

/**
 * Print an already-generated PDF.
 * Desktop / Safari tab: print-js.
 * iOS Home Screen PWA: Share sheet → Print (window.print is blocked on iOS 27+).
 */
export function printPdfUrl(url: string, title = "Print"): void {
  if (!url) return;
  const safeTitle = (title || "Print").replace(/[\\/:*?"<>|]+/g, "_") || "Print";

  if (!isIosHomeScreenPwa()) {
    (printJS as (cfg: { printable: string; type: string }) => void)({
      printable: url,
      type: "pdf",
    });
    return;
  }

  openIosPwaExistingPdfSheet(url, safeTitle);
}

function openIosPwaExistingPdfSheet(url: string, title: string): void {
  removePrintSheet();

  let pdfFile: File | null = null;
  let preparing: Promise<File> | null = null;
  let closed = false;

  const finishClose = () => {
    if (closed) return;
    closed = true;
    removePrintSheet();
  };

  const host =
    (document.querySelector(
      ".MuiDialog-root:last-of-type, .MuiModal-root:last-of-type",
    ) as HTMLElement | null) || document.body;

  const sheet = document.createElement("div");
  sheet.id = SHEET_ID;
  sheet.style.cssText =
    "position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,.45);display:flex;align-items:flex-end;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,Arial,sans-serif;pointer-events:auto;";
  sheet.innerHTML = `
    <div style="width:100%;max-width:480px;background:#fff;border-radius:16px 16px 0 0;padding:18px 16px 28px;box-shadow:0 -8px 32px rgba(0,0,0,.2);">
      <div style="font-size:17px;font-weight:700;color:#111;margin-bottom:6px;">Print</div>
      <div data-status style="font-size:13px;color:#555;line-height:1.4;margin-bottom:14px;">
        Preparing document…
      </div>
      <button type="button" data-print disabled style="width:100%;padding:15px;font-size:17px;font-weight:700;background:#1976d2;color:#fff;border:0;border-radius:12px;opacity:.55;">
        Print
      </button>
      <button type="button" data-close style="width:100%;padding:12px;margin-top:8px;font-size:15px;font-weight:600;background:transparent;color:#444;border:0;">
        Cancel
      </button>
    </div>
  `;
  host.appendChild(sheet);

  const statusEl = sheet.querySelector("[data-status]") as HTMLElement;
  const printBtn = sheet.querySelector("[data-print]") as HTMLButtonElement;
  const closeBtn = sheet.querySelector("[data-close]") as HTMLButtonElement;

  const setReady = () => {
    if (closed) return;
    printBtn.disabled = false;
    printBtn.style.opacity = "1";
    statusEl.textContent =
      "Print dabao → Share sheet me Print choose karo.";
  };

  const setError = (msg: string) => {
    if (closed) return;
    printBtn.disabled = false;
    printBtn.style.opacity = "1";
    statusEl.textContent = msg;
  };

  closeBtn.addEventListener("click", () => finishClose());

  preparing = (async () => {
    const res = await fetch(url);
    const blob = await res.blob();
    const file = new File([blob], `${title}.pdf`, { type: "application/pdf" });
    pdfFile = file;
    return file;
  })()
    .then((file) => {
      setReady();
      return file;
    })
    .catch(() => {
      setError("Document prepare fail. Print dubara dabao.");
      throw new Error("pdf-prepare-failed");
    });

  printBtn.addEventListener("click", async () => {
    if (closed) return;
    printBtn.disabled = true;
    printBtn.textContent = "Opening…";
    statusEl.textContent = "Share sheet khol rahe hain…";

    try {
      const file = pdfFile || (await preparing!);
      const result = await sharePdfFile(file, title);

      if (result === "ok" || result === "cancel") {
        finishClose();
        return;
      }

      printBtn.disabled = false;
      printBtn.textContent = "Print";
      statusEl.textContent =
        "Share sheet block ho gaya. Print ek baar aur dabao.";
    } catch {
      printBtn.disabled = false;
      printBtn.textContent = "Print";
      statusEl.textContent = "Fail hua. Print dubara dabao.";
    }
  });
}
